import Icon from '@components/icons/Icon';
import { StackHeaderProps } from '@react-navigation/stack';
import { Headline3, IconButton, theme } from 'aimo-ui';
import { ButtonTypes } from 'aimo-ui/lib/typescript/src/components/Button/types';
import { HandleComponent } from 'components/sheets/CustomSheetElements';
import useBackButton from 'hooks/useBackButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { CenterColumn } from 'styles/ContainerStyles';
import {
  HeaderButtonWrapper,
  ModalTitleWrapper,
} from 'styles/ModalStackHeaderStyles';
import { testIdentifiers } from 'utils/testIdentifiers';
import SafeAreaHeaderContainer, {
  SafeAreaHeaderContainerProps,
} from './SafeAreaHeaderContainer';

export interface ModalStackHeaderCustomProps
  extends StackHeaderProps,
    SafeAreaHeaderContainerProps {
  background?: string;
  noSafePadding?: boolean;
  closeButton?: boolean;
  forceBackButton?: boolean;
  backButtonType?: ButtonTypes;
  closeButtonType?: ButtonTypes;
  showHandle?: boolean;
  onClose?: () => void;
}

const ModalStackHeaderContainerWithHandle = ({
  showHandle,
  noSafePadding,
  ...props
}: ModalStackHeaderCustomProps) => {
  const padding = () => {
    if (showHandle) {
      return 'horizontal';
    }
    return noSafePadding ? undefined : 'all';
  };
  return (
    <CenterColumn>
      {showHandle && <HandleComponent />}
      <SafeAreaHeaderContainer {...props} extraPadding={padding()} />
    </CenterColumn>
  );
};

const ModalStackHeader = ({
  navigation,
  back,
  background = 'transparent',
  options,
  closeButton = false,
  forceBackButton = false,
  backButtonType = 'tertiary',
  closeButtonType = 'tertiary',
  showHandle = false,
  noSafePadding = false,
  onClose,
  ...props
}: ModalStackHeaderCustomProps) => {
  const { t } = useTranslation();
  const { title, headerTitle } = options;
  const HeaderTitle = headerTitle as any; // ReactNode and JSX.Element incompatibility
  const { headerBackButtonDisplayMode = 'default' } = options || {};
  const showCloseButton =
    closeButton || back?.title === 'mainTabs' || !back?.title;
  const handler = () => {
    navigation.goBack();
    return true;
  };
  useBackButton(handler);

  const handleCloseButtonPress = () => {
    if (onClose) {
      onClose();
    } else if (back?.title) {
      navigation.goBack();
    } else {
      navigation.replace('mainTabs');
    }
  };

  const CustomHeaderButtonWrapper =
    headerBackButtonDisplayMode !== 'minimal' ? View : HeaderButtonWrapper;

  return (
    <ModalStackHeaderContainerWithHandle
      {...props}
      options={options}
      navigation={navigation}
      background={background}
      showHandle={showHandle}
      noSafePadding={noSafePadding}
      leftContent={
        (!showCloseButton || forceBackButton) && (
          // @ts-ignore-next-line
          <CustomHeaderButtonWrapper position="left">
            <IconButton
              rounded={true}
              buttonType={backButtonType}
              onPress={() => navigation.goBack()}
              testID="modal-stack-header-back-button"
              iconComponent={() => (
                <Icon
                  name="back"
                  color={
                    backButtonType === 'secondary'
                      ? theme.colors.white
                      : theme.colors.black
                  }
                />
              )}
              accessibilityLabel={t('goBack')}
            />
          </CustomHeaderButtonWrapper>
        )
      }
      headline={
        headerBackButtonDisplayMode !== 'minimal' && (
          <ModalTitleWrapper>
            {headerTitle ? (
              <HeaderTitle />
            ) : (
              <Headline3 center testID="modal-stack-header-title">
                {title}
              </Headline3>
            )}
          </ModalTitleWrapper>
        )
      }
      rightContent={
        showCloseButton && (
          <View>
            <IconButton
              {...testIdentifiers('modal-stack-header-close-button')}
              rounded={true}
              buttonType={closeButtonType}
              onPress={() => handleCloseButtonPress()}
              iconComponent={() => (
                <Icon
                  name="close"
                  color={
                    closeButtonType === 'secondary'
                      ? theme.colors.white
                      : theme.colors.black
                  }
                />
              )}
              accessibilityLabel={t('button.close')}
            />
          </View>
        )
      }
    />
  );
};

export default ModalStackHeader;
